import api from "./api";

export interface ApiBooking {
  id: number | string;
  date_time: string;
  status: string;
  amount_to_pay?: number | string;
  formated_amount_to_pay?: string;
  amount_paid?: number | string;
  /** Expected deposit amount (front-end should show it between Total and Paid). */
  deposit?: number | string;
  customer?: {
    id?: number | string;
    name?: string;
    email?: string;
    mobile?: string;
    phone?: string;
    user_image_url?: string;
  };
  pending_amount?: number | string;
  change_due?: number | string;
  currency?: string;
  location?: string | {
    id?: number | string;
    name?: string;
  };
  user?: {
    id?: number | string;
    name?: string;
    email?: string;
    mobile?: string;
    phone?: string;
    user_image_url?: string;
  };
  users?: Array<{
    id?: number | string;
    name?: string;
    first_name?: string;
    last_name?: string;
    user_image_url?: string;
  }>;
  employees?: Array<{
    id?: number | string;
    name?: string;
    first_name?: string;
    last_name?: string;
  }>;
  customer_name?: string;
  name?: string;
  employee?: {
    id?: number | string;
    name?: string;
    first_name?: string;
    last_name?: string;
  };
  employee_name?: string;
  staff?: {
    id?: number | string;
    name?: string;
    first_name?: string;
    last_name?: string;
  };
  staff_name?: string;
  company?: {
    currency?: {
      currency_symbol?: string;
      currency_code?: string;
    };
  };
  items?: any[];
  consultation_form_response?: ConsultationForm[];
  [key: string]: unknown;
}

export interface ConsultationForm {
  id: number;
  form_id: number;
  booking_id: number;
  sunbed_id: number | null;
  company_id: number;
  user_id: number;
  submitted_at: string | null;
  signature: string | null;
  created_at: string;
  updated_at: string;
  form: {
    id: number;
    uuid: string;
    company_id: number;
    title: string;
    description: string;
    frequency: string;
    auto_send: boolean;
    status: string;
    created_at: string;
    updated_at: string;
  };
}

interface ApiBookingsResponse {
  success: boolean;
  data?: {
    data?: ApiBooking[];
    current_page?: number;
    last_page?: number;
    total?: number;
    per_page?: number;
  } | ApiBooking[];
  message?: string;
}

export type BookingStatus = "Booked" | "Pending" | "Completed" | "In Progress" | "No Show" | "Cancelled";

export interface Booking {
  id: number;
  customerName: string;
  employeeName: string;
  bookingTime: string; // Format: "2025-08-02 08:00 AM"
  total: number;
  deposit: number;
  paid: number;
  pending: number;
  status: BookingStatus;
  customerId: number;
  consultation_forms?: ConsultationForm[];
  rawData?: ApiBooking; // Store raw API data for reference
}

const normalizeStatus = (apiStatus: string | null | undefined): BookingStatus => {
  if (!apiStatus) return "Pending";

  const statusLower = apiStatus.toLowerCase();
  if (statusLower === "booked" || statusLower === "confirmed") return "Booked";
  if (statusLower === "pending") return "Pending";
  if (statusLower === "completed" || statusLower === "done") return "Completed";
  if (statusLower === "in progress" || statusLower === "in_progress") return "In Progress";
  if (statusLower === "no show" || statusLower === "no_show") return "No Show";
  if (statusLower === "cancelled" || statusLower === "canceled") return "Cancelled";

  return "Booked";
};

const extractName = (obj: any): string | null => {
  if (!obj) return null;
  if (typeof obj === "string") return obj;
  if (obj.name) return obj.name;
  if (obj.first_name || obj.last_name) {
    return [obj.first_name, obj.last_name].filter(Boolean).join(" ").trim();
  }
  if (obj.customer_name) return obj.customer_name;
  if (obj.employee_name) return obj.employee_name;
  if (obj.staff_name) return obj.staff_name;
  return null;
};

const getEmployeeName = (booking: ApiBooking): string => {
  // Check top-level name fields
  const topLevelName = booking.employee_name || booking.staff_name;
  if (topLevelName) return String(topLevelName);

  // Check employees array — collect ALL names (multiple stylists for multiple services)
  if (Array.isArray(booking.employees) && booking.employees.length > 0) {
    const names = booking.employees
      .map((e) => extractName(e))
      .filter((n): n is string => Boolean(n));
    // Deduplicate in case same stylist assigned to multiple services
    const unique = Array.from(new Set(names));
    if (unique.length > 0) return unique.join(", ");
  }

  // Check users array (common for assigned staff/stylists)
  if (Array.isArray(booking.users) && booking.users.length > 0) {
    const names = booking.users
      .map((u) => extractName(u))
      .filter((n): n is string => Boolean(n));
    const unique = Array.from(new Set(names));
    if (unique.length > 0) return unique.join(", ");
  }

  // Check singular objects
  const empName = extractName(booking.employee) || extractName(booking.staff);
  if (empName) return empName;

  // Check items for employee info
  if (Array.isArray(booking.items) && booking.items.length > 0) {
    const names: string[] = [];
    for (const item of booking.items) {
      const itemEmpName = extractName(item.employee) || extractName(item.staff) || extractName(item.business_service?.users?.[0]);
      if (itemEmpName) names.push(itemEmpName);
    }
    const unique = Array.from(new Set(names));
    if (unique.length > 0) return unique.join(", ");
  }

  // Check for common nested structures
  if (booking.booking_details && typeof booking.booking_details === 'object') {
    const details = booking.booking_details as any;
    const detailsEmpName = extractName(details.employee) || extractName(details.staff) || details.employee_name || details.staff_name;
    if (detailsEmpName) return detailsEmpName;
  }

  return "Unknown Employee";
};

const getCustomerName = (booking: ApiBooking): string => {
  if (booking.customer_name) return booking.customer_name;
  if (booking.name) return booking.name;

  // Check customer object
  const cusName = extractName(booking.customer);
  if (cusName) return cusName;

  // Check user object (single booking response uses 'user' for customer)
  const userName = extractName(booking.user);
  if (userName) return userName;

  return "Unknown Customer";
};

/**
 * Heuristic to identify if a booking is a "service booking" vs. other types (products, etc.)
 * Used to fulfill user request: "make sure bookings only shows services booking not any other"
 */
const isServiceBooking = (item: ApiBooking): boolean => {
  // 0. Explicit check for sunbed type if available from backend
  if ((item as any).booking_type === "sunbed") {
    return false;
  }

  // 1. Check items first for explicit markers
  if (Array.isArray(item.items) && item.items.length > 0) {
    // If ANY item is a sunbed, we treat this as a sunbed booking and hide it from the services list
    if (item.items.some((i: any) => i.sunbed_id !== null && i.sunbed_id !== undefined)) {
      return false;
    }

    // Check for explicit service pointers
    return item.items.some((i: any) =>
      (i.business_service_id !== null && i.business_service_id !== undefined) ||
      i.business_service ||
      i.type === "service" ||
      i.service_id
    );
  }

  // 2. Fallback: If no items, check for employee assignment
  // Service bookings (appointments) almost always have an employee assigned.
  // We'll also check if it's explicitly marked as a "sunbed" booking in some way if possible.
  const hasEmployee = !!(
    item.employee_name ||
    item.staff_name ||
    (Array.isArray(item.employees) && item.employees.length > 0) ||
    (Array.isArray(item.users) && item.users.length > 0) ||
    item.employee ||
    item.staff
  );

  return hasEmployee;
};

const parseAmount = (value: number | string | undefined | null): number => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return value;
  const parsed = parseFloat(String(value));
  return isNaN(parsed) ? 0 : parsed;
};

const mapApiBookingToBooking = (item: ApiBooking): Booking => {
  const total = parseAmount(item.amount_to_pay);
  const status = normalizeStatus(item.status);

  // If status is Completed, payment is fully done
  let paid = parseAmount(item.amount_paid);
  let pending = Math.max(0, total - paid);

  if (status === "Completed") {
    paid = total;
    pending = 0;
  }

  // Deposit is optional in API. We default to 0.
  // Backend may use slightly different spellings depending on the endpoint.
  const deposit = parseAmount(
    (item as any).deposit ??
      (item as any).deposite ??
      (item as any).deposte,
  );

  const parsedId = typeof item.id === "number" ? item.id : parseInt(String(item.id), 10);

  // Ensure customer data is normalized in rawData if it came as 'user'
  const normalizedItem = {
    ...item,
    customer: item.customer || item.user,
  };

  return {
    id: Number.isNaN(parsedId) ? 0 : parsedId,
    customerName: getCustomerName(item),
    employeeName: getEmployeeName(item),
    bookingTime: item.date_time || "Unknown Time", // Backend already sends formatted time
    total,
    deposit,
    paid,
    pending,
    status,
    customerId: Number(item.customer?.id || item.user?.id || 0),
    consultation_forms: item.consultation_form_response,
    rawData: normalizedItem,
  };
}

export const getBookings = async (params?: {
  page?: number;
  per_page?: number;
  status?: string;
  customer_id?: number | string;
  employee_id?: number | string;
  date?: string;
  search?: string;
}): Promise<{ bookings: Booking[]; total?: number; currentPage?: number; lastPage?: number }> => {
  // IMPORTANT: Backend doesn't paginate bookings, so we:
  // 1. Try to get all bookings from /admin/bookings/services-bookings endpoint (FASTEST - uses backend filters)
  // 2. If that fails, fetch limited customer pages and aggregate bookings (then apply client-side filters)
  // 3. Then do client-side pagination on the filtered results

  try {
    const queryParams = new URLSearchParams();
    // Don't send page/per_page to backend since it doesn't paginate
    // We'll get all bookings and paginate on the client side
    // Map frontend filter names to backend filter names (based on backend code)
    if (params?.status && params.status !== "View All") {
      // Map frontend status values to backend
      const statusMap: Record<BookingStatus, string> = {
        'Booked': 'booked',
        'Pending': 'pending',
        'Completed': 'completed',
        'In Progress': 'in_progress',
        'No Show': 'no_show',
        'Cancelled': 'cancelled',
      };
      const backendStatus = statusMap[params.status as BookingStatus] || params.status.toLowerCase();
      // Try both filter_status and status (backend supports both)
      queryParams.append("filter_status", backendStatus);
      queryParams.append("status", backendStatus);
    }
    if (params?.customer_id) {
      queryParams.append("filter_customer", String(params.customer_id));
    }
    if (params?.employee_id) {
      queryParams.append("filter_employee", String(params.employee_id));
    }
    if (params?.date) {
      // Backend expects date in d-m-Y format for filter_date
      const dateObj = new Date(params.date + 'T00:00:00'); // Ensure correct timezone
      const formattedDate = `${String(dateObj.getDate()).padStart(2, '0')}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${dateObj.getFullYear()}`;
      queryParams.append("filter_date", formattedDate);
    }
    // Note: search parameter might not be supported by backend, will apply client-side if needed

    const queryString = queryParams.toString();
    const endpoint = `/admin/bookings/services-bookings${queryString ? `?${queryString}` : ""}`;

    console.log("Attempting bookings endpoint (FASTEST - uses backend filters):", endpoint);

    const response = await api.get<any>(endpoint);
    const payload = response.data;

    console.log("Bookings API response structure:", {
      hasPayload: !!payload,
      isPayloadArray: Array.isArray(payload),
      payloadKeys: payload && typeof payload === 'object' ? Object.keys(payload) : [],
    });

    let allBookingsFromApi: ApiBooking[] = [];

    // Aggressive but safe response parsing
    if (Array.isArray(payload)) {
      // Structure: [...]
      allBookingsFromApi = payload;
    } else if (payload && typeof payload === 'object') {
      // Structure: { data: [...] } OR { success: true, data: [...] }
      if (Array.isArray(payload.data)) {
        allBookingsFromApi = payload.data;
      }
      // Structure: { data: { data: [...] } } OR { success: true, data: { data: [...] } }
      else if (payload.data && typeof payload.data === 'object' && Array.isArray(payload.data.data)) {
        allBookingsFromApi = payload.data.data;
      }
      // Structure: { success: true, bookings: [...] }
      else if (Array.isArray(payload.bookings)) {
        allBookingsFromApi = payload.bookings;
      }
    }

    // Still no bookings found? Check if it's a structural mismatch and fallback
    if (allBookingsFromApi.length === 0 && (!payload || (typeof payload === 'object' && !payload.data && !Array.isArray(payload)))) {
      console.warn("Response not recognized as a bookings list, falling back to aggregate from customers");
      return getBookingsFromCustomers(params);
    }

    console.log(`Got ${allBookingsFromApi.length} bookings from /admin/bookings/services-bookings endpoint`);
    if (allBookingsFromApi.length > 0) {
      console.log("Sample raw booking from API:", allBookingsFromApi[0]);
      console.log("Sample raw booking keys:", Object.keys(allBookingsFromApi[0]));
    }

    // Apply service-only filter BEFORE pagination for accurate counts
    const serviceBookings = allBookingsFromApi.filter(isServiceBooking);
    console.log(`Filtered to ${serviceBookings.length} service bookings (from ${allBookingsFromApi.length} total)`);

    // Map to frontend objects
    let filteredBookings = serviceBookings.map(mapApiBookingToBooking);

    // Apply client-side search filter if provided
    if (params?.search) {
      const searchLower = params.search.toLowerCase();
      filteredBookings = filteredBookings.filter(booking => {
        return booking.customerName.toLowerCase().includes(searchLower) ||
          booking.employeeName.toLowerCase().includes(searchLower) ||
          booking.id.toString().includes(searchLower);
      });
    }

    // Apply client-side pagination (backend doesn't paginate)
    const page = params?.page || 1;
    const perPageSize = params?.per_page || 10;
    const startIndex = (page - 1) * perPageSize;
    const endIndex = startIndex + perPageSize;
    const paginatedBookings = filteredBookings.slice(startIndex, endIndex);
    const totalPages = Math.ceil(filteredBookings.length / perPageSize) || 1;

    console.log(`Client-side pagination: Returning ${paginatedBookings.length} bookings for page ${page} of ${totalPages} (total: ${filteredBookings.length})`);

    return {
      bookings: paginatedBookings,
      total: filteredBookings.length,
      currentPage: page,
      lastPage: totalPages,
    };
  } catch (error: any) {
    // Always fallback to customers endpoint
    console.log("Bookings endpoint failed, using customers endpoint:", error.response?.status || error.message);
    return getBookingsFromCustomers(params);
  }
};

// Aggregate bookings from customers endpoint
// Note: Backend doesn't paginate bookings, so we fetch customers and do client-side pagination
// OPTIMIZED: Limit pages to prevent slow loading
const getBookingsFromCustomers = async (params?: {
  page?: number;
  per_page?: number;
  status?: string;
  customer_id?: number | string;
  employee_id?: number | string;
  date?: string;
  search?: string;
}): Promise<{ bookings: Booking[]; total?: number; currentPage?: number; lastPage?: number }> => {
  try {
    // OPTIMIZATION: Limit to max pages to prevent slow loading
    // Fetch enough customers to get reasonable booking coverage
    const MAX_CUSTOMER_PAGES = 10; // Limit to 10 pages max (10,000 customers)
    const perPage = 1000; // Fetch 1000 customers per page
    let allCustomers: any[] = [];
    let currentPageNum = 1;
    let hasMorePages = true;

    console.log(`Fetching customers (max ${MAX_CUSTOMER_PAGES} pages) to aggregate bookings...`);

    // Fetch limited pages of customers (not all to avoid slow loading)
    while (hasMorePages && currentPageNum <= MAX_CUSTOMER_PAGES) {
      const queryParams = new URLSearchParams();
      queryParams.append("per_page", String(perPage));
      queryParams.append("page", String(currentPageNum));

      const endpoint = `/admin/customers?${queryParams.toString()}`;
      console.log(`Fetching customers page ${currentPageNum}/${MAX_CUSTOMER_PAGES}...`);

      const response = await api.get(endpoint);
      const result = response.data;

      // Handle different response structures
      let pageCustomers: any[] = [];

      if (result?.success && result.data) {
        // Check if data.data exists (nested structure - paginated)
        if (result.data.data && Array.isArray(result.data.data)) {
          pageCustomers = result.data.data;
        }
        // Check if data is directly an array
        else if (Array.isArray(result.data)) {
          pageCustomers = result.data;
        }
        // Check if there's a data property that's an array
        else if (Array.isArray((result.data as any).data)) {
          pageCustomers = (result.data as any).data;
        }
      }

      if (pageCustomers.length === 0) {
        hasMorePages = false;
        break;
      }

      allCustomers = allCustomers.concat(pageCustomers);
      console.log(`Fetched ${pageCustomers.length} customers from page ${currentPageNum}, total: ${allCustomers.length}`);

      // Check if there are more pages (but respect MAX limit)
      if (result?.success && result.data) {
        const lastPage = (result.data as any).last_page || 1;
        const currentPage = (result.data as any).current_page || currentPageNum;
        hasMorePages = currentPage < lastPage && currentPageNum < MAX_CUSTOMER_PAGES;
        if (hasMorePages) {
          currentPageNum++;
        } else {
          if (currentPageNum >= MAX_CUSTOMER_PAGES) {
            console.warn(`Reached max customer pages limit (${MAX_CUSTOMER_PAGES}). Some bookings may not be included.`);
          } else {
            console.log(`Reached last page (${lastPage}), total customers fetched: ${allCustomers.length}`);
          }
        }
      } else {
        // If no pagination info, assume we got all if less than per_page
        hasMorePages = pageCustomers.length === perPage && currentPageNum < MAX_CUSTOMER_PAGES;
        if (hasMorePages) {
          currentPageNum++;
        }
      }
    }

    if (allCustomers.length === 0) {
      console.warn("No customers found");
      return { bookings: [], total: 0, currentPage: 1, lastPage: 1 };
    }

    console.log(`Processing ${allCustomers.length} customers for bookings (optimized: limited to ${MAX_CUSTOMER_PAGES} pages)`);

    const customers = allCustomers;

    const allBookings: ApiBooking[] = [];

    // Extract bookings from all customers
    let totalCustomersWithBookings = 0;
    let totalBookingsBeforeFilter = 0;

    customers.forEach((customer: any) => {
      if (customer.customer_bookings && Array.isArray(customer.customer_bookings) && customer.customer_bookings.length > 0) {
        totalCustomersWithBookings++;
        totalBookingsBeforeFilter += customer.customer_bookings.length;
        console.log(`Customer ${customer.name || customer.id} has ${customer.customer_bookings.length} bookings`);

        customer.customer_bookings.forEach((booking: any) => {
          // Apply customer filter first (before enriching to save processing)
          if (params?.customer_id && String(customer.id) !== String(params.customer_id)) {
            return;
          }

          // Add customer info to booking
          const enrichedBooking: ApiBooking = {
            ...booking,
            customer: {
              id: customer.id,
              name: customer.name,
              email: customer.email,
            },
            customer_name: customer.name,
          };

          // Apply filters
          if (params?.status) {
            const bookingStatus = (booking.status || '').toLowerCase();
            const filterStatus = params.status.toLowerCase();
            // Map status values
            const statusMap: Record<string, string[]> = {
              booked: ['booked', 'confirmed'],
              pending: ['pending'],
              completed: ['completed', 'done'],
              'in progress': ['in progress', 'in_progress'],
              'no show': ['no show', 'no_show'],
              cancelled: ['cancelled', 'canceled'],
            };
            const matchingStatuses = statusMap[filterStatus] || [filterStatus];
            if (!matchingStatuses.includes(bookingStatus)) {
              return;
            }
          }
          if (params?.date && booking.date_time && !booking.date_time.startsWith(params.date)) {
            return;
          }
          if (params?.employee_id) {
            // Try to match employee_id from booking data
            const bookingEmployeeId = booking.employee_id || booking.employee?.id || booking.staff_id || booking.staff?.id;
            if (String(bookingEmployeeId) !== String(params.employee_id)) {
              return;
            }
          }
          if (params?.search) {
            const searchLower = params.search.toLowerCase();
            const matchesSearch =
              customer.name?.toLowerCase().includes(searchLower) ||
              String(booking.id || '').includes(searchLower) ||
              booking.employee_name?.toLowerCase().includes(searchLower) ||
              booking.staff_name?.toLowerCase().includes(searchLower) ||
              booking.employee?.name?.toLowerCase().includes(searchLower) ||
              booking.staff?.name?.toLowerCase().includes(searchLower);
            if (!matchesSearch) return;
          }

          // Apply service-only filter
          if (!isServiceBooking(enrichedBooking)) {
            return;
          }

          allBookings.push(enrichedBooking);
        });
      } else {
        // Log customers without bookings for debugging
        if (!customer.customer_bookings) {
          console.log(`Customer ${customer.name || customer.id} has no customer_bookings property`);
        } else if (!Array.isArray(customer.customer_bookings)) {
          console.log(`Customer ${customer.name || customer.id} has customer_bookings but it's not an array:`, typeof customer.customer_bookings);
        }
      }
    });

    console.log(`Summary: ${totalCustomersWithBookings} customers with bookings, ${totalBookingsBeforeFilter} total bookings before filtering`);

    console.log(`Total bookings found after filtering: ${allBookings.length} (out of ${totalBookingsBeforeFilter} before filtering)`);

    if (allBookings.length === 0 && totalBookingsBeforeFilter === 0) {
      console.warn("No bookings found in any customer. This might indicate:");
      console.warn("1. The API response structure is different than expected");
      console.warn("2. Customers don't have the customer_bookings property");
      console.warn("3. The customer_bookings arrays are all empty");
      console.warn("Sample customer structure:", customers[0] ? Object.keys(customers[0]) : "No customers");
    }

    // Sort by date_time (most recent first)
    allBookings.sort((a, b) => {
      try {
        const dateA = new Date(a.date_time).getTime();
        const dateB = new Date(b.date_time).getTime();
        return dateB - dateA;
      } catch (e) {
        return 0;
      }
    });

    // Apply pagination
    const page = params?.page || 1;
    const perPageSize = params?.per_page || 10;
    const startIndex = (page - 1) * perPageSize;
    const endIndex = startIndex + perPageSize;
    const paginatedBookings = allBookings.slice(startIndex, endIndex);
    const totalPages = Math.ceil(allBookings.length / perPageSize) || 1;

    console.log(`Returning ${paginatedBookings.length} bookings for page ${page} of ${totalPages}`);

    const mappedBookings = paginatedBookings.map(mapApiBookingToBooking);
    console.log("Mapped bookings sample:", mappedBookings.slice(0, 2));

    return {
      bookings: mappedBookings,
      total: allBookings.length,
      currentPage: page,
      lastPage: totalPages,
    };
  } catch (error: any) {
    console.error("Error fetching bookings from customers:", error);
    console.error("Error response:", error.response?.data);
    console.error("Error message:", error.message);
    throw error; // Re-throw so the component can handle it
  }
};

export const getBookingById = async (id: number | string): Promise<Booking | null> => {
  try {
    const response = await api.get<any>(`/admin/bookings/${id}`);
    const data = response.data;
    if (data?.booking) {
      // Some booking-detail endpoints return payment aggregates at top-level
      // (e.g. total_paid, total_pending). Merge them into raw booking payload
      // so detail drawers can render accurate Due/Paid values.
      const mergedBooking = {
        ...data.booking,
        total_paid: data.total_paid ?? data.booking.total_paid ?? data.booking.amountPaid,
        total_pending: data.total_pending ?? data.booking.total_pending ?? data.booking.pending_amount,
      };
      return mapApiBookingToBooking(mergedBooking);
    }
    if (data?.success && data?.data) {
      return mapApiBookingToBooking(data.data);
    }
    return null;
  } catch (error) {
    console.error("Error fetching booking by id:", error);
    return null;
  }
};

export const assignConsultationForm = async (
  bookingId: number,
  payload: { customer_id: number; form_ids: number[] }
) => {
  try {
    const response = await api.post(`/admin/bookings/assign-consultation-forms/${bookingId}`, payload);
    return response.data;
  } catch (error) {
    console.error("Error assigning consultation forms:", error);
    throw error;
  }
};

export const deleteBooking = async (id: number | string): Promise<void> => {
  const response = await api.delete(`/admin/bookings/${id}`);
  // Backend may return { message: '...' } or success response
  // If no error is thrown, deletion was successful
  return;
};

export const updateBookingStatus = async (
  id: number | string,
  status: string
): Promise<Booking> => {
  const response = await api.post(`/admin/bookings/${id}/status`, { status });
  if (response.data?.success && response.data.data) {
    return mapApiBookingToBooking(response.data.data);
  }
  throw new Error("Failed to update booking status");
};

export interface UpdateBookingPayload {
  booking_date: string; // Format: Y-m-d
  hidden_booking_time: string; // Format: "H:i a" (e.g., "02:21 AM")
  status: BookingStatus;
  employee_id?: number[]; // Array of employee IDs
  item_ids?: (string | number)[]; // Array of service/deal IDs
  types?: string[]; // Array of types ('service' or 'deal')
  cart_quantity?: number[]; // Array of quantities for services/deals
  item_prices?: number[]; // Array of prices for services/deals
  cart_products?: (string | number)[]; // Array of product IDs
  product_quantity?: number[]; // Array of product quantities
  product_prices?: number[]; // Array of product prices
  cart_discount?: number; // Discount percentage (0-100)
  payment_status?: string; // 'pending' or 'completed'
  tax_amount?: number; // Optional, backend calculates if not provided
  coupon_id?: number | string | null;
  coupon_amount?: number;
  paid_amount?: number;
}

export const updateBooking = async (
  id: number | string,
  payload: UpdateBookingPayload
): Promise<Booking> => {
  const response = await api.put<{ message?: string; booking?: ApiBooking }>(`/admin/bookings/${id}`, payload);

  // Backend returns { message: '...', booking: {...} }
  if (response.data?.booking) {
    return mapApiBookingToBooking(response.data.booking);
  }

  // Fallback: try success/data structure
  if ((response.data as any)?.success && (response.data as any)?.data) {
    const bookingData = Array.isArray((response.data as any).data)
      ? (response.data as any).data[0]
      : (response.data as any).data;
    return mapApiBookingToBooking(bookingData as ApiBooking);
  }

  throw new Error(response.data?.message || "Failed to update booking");
};

// Quick Payment API
export interface QuickPaymentPayload {
  emp_id: number | null;
  amount: number;
  payment_type: "cash" | "card";
  trans_id?: string | null;
}

export interface QuickPaymentResponse {
  status: "success" | "error";
  message: string;
}

export const addQuickPayment = async (
  payload: QuickPaymentPayload
): Promise<QuickPaymentResponse> => {
  const response = await api.post<QuickPaymentResponse>(
    "/admin/bookings/add-quick-payment",
    payload
  );
  return response.data;
};

// Sold Products API
export interface SoldProduct {
  id: number;
  location_id: number;
  company_id: number;
  name: string;
  description: string;
  sku: string | null;
  barcode: string | null;
  price: number;
  discount: number;
  discount_type: string;
  image: string[];
  default_image: string;
  status: string;
  product_image_url: string;
  converted_price: number;
  converted_discounted_price: number;
  formated_price: string;
  formated_discounted_price: string;
  discounted_price: number;
  product_taxes?: Array<{
    id: number;
    tax_id: number;
    product_id: number;
    company_id: number | null;
    tax?: {
      id: number;
      name: string;
      percent: number;
      status: string;
    };
  }>;
  /** 1 = used in store; show "Used" in Store usage column */
  in_store_usage?: number;
  /** Booking item pivot data (quantity/unit_price/amount as sold, not catalog values) */
  pivot?: {
    quantity?: number;
    unit_price?: number;
    amount?: number;
    [key: string]: unknown;
  };
  /** Some API responses embed these directly on the product */
  booking_quantity?: number;
  booking_unit_price?: number;
  booking_amount?: number;
}

export interface SoldProductEmployee {
  id: number;
  company_id: number;
  name: string;
  email: string;
  mobile: string;
  user_image_url: string;
}

export interface SoldProductBooking {
  id: number;
  products: SoldProduct[] | { [key: string]: SoldProduct };
  name: string;
  date_time: string;
  amount_to_pay: number;
  amount_paid: number;
  employees: SoldProductEmployee[];
  location: string;
  pending_amount: number;
  change_due: number;
  status: string;
  currency: string;
  /** e.g. "Cash", "Card", "Cheque", "Multiple" - from booking payment */
  payment_gateway?: string;
  /** Alternative field some APIs return */
  payment_type?: string;
  /** Per-tender payments; type on first item used when payment_gateway missing */
  booking_payments?: Array<{ type?: string; payment_type?: string; [key: string]: unknown }>;
  action: {
    edit?: string;
    view?: string;
    delete?: number;
  };
}

export interface SoldProductsResponse {
  data: SoldProductBooking[];
}

export const getSoldProducts = async (): Promise<SoldProductsResponse> => {
  const response = await api.get<SoldProductsResponse>(
    "/admin/bookings/booking-product-sold"
  );
  return response.data;
};

// Service (appointment) bookings for Services Sales Report
export interface ServiceBookingEmployee {
  id: number;
  name: string;
  email?: string;
  [key: string]: unknown;
}

/** Nested business service on a booking line item (may be null when service was deleted) */
export interface ServiceBookingBusinessService {
  id: number;
  name: string;
  [key: string]: unknown;
}

/** Single line item on a service booking (each item is one service) */
export interface ServiceBookingLineItem {
  id: number;
  business_service_id: number | null;
  business_service: ServiceBookingBusinessService | null;
  quantity?: number;
  unit_price?: number;
  amount?: number;
  [key: string]: unknown;
}

export interface ServiceBookingItem {
  id: number;
  name: string;
  date_time: string;
  amount_to_pay: number;
  amount_paid: number;
  employees: ServiceBookingEmployee[];
  location: string;
  pending_amount: number;
  change_due: number;
  status: string;
  currency: string;
  action?: { edit?: string; view?: string; delete?: number };
  /** Line items: each item has business_service (object or null) and business_service_id */
  items?: ServiceBookingLineItem[];
}

export interface ServiceBookingsResponse {
  data: ServiceBookingItem[];
  customers?: unknown[];
  employees?: unknown[];
  locations?: unknown[];
}

export const getServiceBookings = async (): Promise<ServiceBookingsResponse> => {
  const response = await api.get<ServiceBookingsResponse>(
    "/admin/bookings/services-bookings"
  );
  return response.data;
};
